// The "Demo" strip of the single HikmahVerses demo account (modelled on Quranic Poems' DemoViewSwitcher).
//
// Shown ONLY while signed in as one of the seeded demo accounts (the gateway says so: GET /demo/status); for every
// real user it renders nothing and sends nothing but that one status call. It swaps the signed-in identity for another
// view of the same demo - Member, Student, Teacher or Principal - without signing out, then reloads so the whole app
// (and its role-based menus) starts over as that person. The gateway keeps the dashboard's session in step, so the
// other apps pick the same view up when they are opened from the dashboard.
//
// This file is intentionally identical in every app and on the dashboard (no imports from the host, its own scoped
// styles). The host passes: gatewayUrl, getToken() (sync, may return null), setToken(t) (store the new token, or omit
// it where the session cookie alone is the login), homes ({ default, principal, teacher, student, normal }: where to
// land after switching), bottomOffset (px to lift the strip above the host's own bottom tab bar, when it has one that
// does not publish --mobile-tabbar-h), compact (icons only, except the current view, for hosts with little room).
import React, { useEffect, useState } from "react";

const LABELS = { normal: "Member", student: "Student", teacher: "Teacher", principal: "Principal", free: "Free", premium: "Premium", family: "Family" };
const ICON = { normal: "👤", student: "🎒", teacher: "🧑‍🏫", principal: "🏫", free: "🌱", premium: "⭐", family: "👨‍👩‍👧" };
const CSS = `
.hvdemo{position:fixed;left:50%;bottom:calc(var(--mobile-tabbar-h, 0px) + env(safe-area-inset-bottom, 0px) + 18px);transform:translateX(-50%);z-index:2147483000;display:flex;align-items:center;gap:4px;max-width:calc(100vw - 16px);
  background:rgba(255,255,255,.96);backdrop-filter:blur(6px);border:1px solid #D5F2E0;border-radius:999px;box-shadow:0 10px 30px rgba(0,0,0,.14);padding:5px 6px;font-family:system-ui,-apple-system,Segoe UI,sans-serif}
.hvdemo .tag{font-size:10px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#5F6B60;padding:0 6px 0 10px}
.hvdemo button{border:0;background:none;color:#5F6B60;font:inherit;font-size:13px;font-weight:700;padding:7px 11px;border-radius:999px;cursor:pointer;display:inline-flex;align-items:center;gap:5px;white-space:nowrap}
.hvdemo button:hover:not(:disabled){background:#F5FDF8}
.hvdemo button[aria-pressed=true]{background:#4ECB7E;color:#fff;cursor:default}
.hvdemo button:disabled{opacity:.6}
.hvdemo .x{padding:7px 9px;color:#9AA59C;font-size:12px}
.hvdemo .sep{width:1px;height:22px;background:#D5F2E0;margin:0 4px;flex:none}
.hvdemo .plans{display:inline-flex;align-items:center;gap:2px}
.hvdemo .plans button{font-size:12px;padding:6px 9px}
.hvdemo .plans button[aria-pressed=true]{background:#F5C755;color:#3B3000}
.hvdemo.mini{left:12px;transform:none;padding:4px;gap:0}
.hvdemo.mini button{padding:8px 10px;font-size:18px}
@media (max-width:520px){.hvdemo{left:8px;transform:none}.hvdemo .tag{display:none}.hvdemo button{padding:7px 9px;font-size:12px}.hvdemo button:not([aria-pressed=true]) .lbl{display:none}}
.hvdemo.compact button:not([aria-pressed=true]) .lbl{display:none}
`;

export default function GatewayDemoSwitcher({ gatewayUrl, getToken, setToken, homes = {}, bottomOffset = 0, compact = false }) {
  const base = String(gatewayUrl || "").replace(/\/+$/, "");
  const [token, setTok] = useState(() => getToken());
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(null);
  // Hide the strip when it is in the way (a floating chat button, an install banner); the choice is remembered.
  const [mini, setMini] = useState(() => { try { return localStorage.getItem("hv_demo_mini") === "1"; } catch { return false; } });
  const setMiniSaved = (v) => { setMini(v); try { localStorage.setItem("hv_demo_mini", v ? "1" : "0"); } catch { /* private mode */ } };

  // Notice a token that arrives or changes after mount (e.g. the dashboard fetches its session a moment after load).
  useEffect(() => {
    const id = setInterval(() => { const t = getToken(); setTok((p) => (p === t ? p : t)); }, 1500);
    return () => clearInterval(id);
  }, [getToken]);

  useEffect(() => {
    let alive = true;
    if (!token) { setInfo(null); return undefined; }
    fetch(`${base}/demo/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : { demo: false }))
      .then((j) => alive && setInfo(j.demo ? j : null))
      .catch(() => alive && setInfo(null));
    return () => { alive = false; };
  }, [base, token]);

  if (!info) return null;

  const go = async (target) => {
    if (busy || info.slot === target) return;
    setBusy(target);
    try {
      const r = await fetch(`${base}/demo/switch-view`, {
        method: "POST", credentials: "include",   // credentials: the gateway replaces its session cookie with the new view's
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify({ target }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Couldn't switch");
      if (setToken) setToken(j.token);
      // A full load, not a client-side navigate: every page must refetch as the new person.
      const dest = new URL(homes[target] || homes.default || "/", window.location.href);
      const hashOnly = dest.pathname === window.location.pathname && dest.search === window.location.search && dest.hash !== window.location.hash;
      window.location.assign(dest.href);
      if (hashOnly) window.location.reload();   // assigning a URL that differs only by #fragment would not reload
    } catch (e) {
      setBusy(null);
      window.alert(e.message || "Couldn't switch that view - try again.");
    }
  };

  // The Parent view of the demo is an individual account: pick the plan it is on (Free shows the trial view, Premium and Family open
  // everything). Only the demo account has this, and only while it is in its Parent view; the page reloads on the new plan.
  const pickPlan = async (plan) => {
    if (busy || info.plan === plan) return;
    setBusy(`plan:${plan}`);
    try {
      const r = await fetch(`${base}/demo/switch-plan`, {
        method: "POST", credentials: "include",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify({ plan }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Couldn't change the plan");
      window.location.reload();
    } catch (e) {
      setBusy(null);
      window.alert(e.message || "Couldn't change the plan - try again.");
    }
  };

  const bottomStyle = bottomOffset ? { marginBottom: bottomOffset } : undefined;
  if (mini) {
    return (
      <div className="hvdemo mini" data-testid="demo-switcher" role="group" aria-label="Demo view" style={bottomStyle}>
        <style>{CSS}</style>
        <button onClick={() => setMiniSaved(false)} data-testid="demo-expand" title={`Demo: ${LABELS[info.slot] || "view"} - click to switch view`} aria-label="Show demo view switcher">
          <span aria-hidden>{ICON[info.slot] || "🎭"}</span>
        </button>
      </div>
    );
  }

  return (
    <div className={compact ? "hvdemo compact" : "hvdemo"} data-testid="demo-switcher" role="group" aria-label="Demo view" style={bottomStyle}>
      <style>{CSS}</style>
      <span className="tag">Demo</span>
      {info.views.map((v) => (
        <button key={v.target} aria-pressed={info.slot === v.target} disabled={!!busy} onClick={() => go(v.target)} data-testid={`demo-view-${v.target}`} title={v.label} aria-label={v.label}>
          <span aria-hidden>{ICON[v.target]}</span><span className="lbl">{busy === v.target ? "..." : v.label}</span>
        </button>
      ))}
      {info.plans && info.plans.length > 0 && (<>
        <span className="sep" aria-hidden />
        <span className="plans" role="group" aria-label="Demo plan" data-testid="demo-plans">
          <span className="tag" style={{ padding: "0 4px" }}>Plan</span>
          {info.plans.map((p) => (
            <button key={p.target} aria-pressed={info.plan === p.target} disabled={!!busy} onClick={() => pickPlan(p.target)} data-testid={`demo-plan-${p.target}`} title={`${p.label} plan`} aria-label={`${p.label} plan`}>
              <span aria-hidden>{ICON[p.target]}</span><span className="lbl">{busy === `plan:${p.target}` ? "..." : p.label}</span>
            </button>
          ))}
        </span>
      </>)}
      <button className="x" onClick={() => setMiniSaved(true)} data-testid="demo-collapse" title="Hide" aria-label="Hide demo view switcher">✕</button>
    </div>
  );
}
